<script setup lang="ts">
/**
 * L'encouragement d'un parent, découvert par l'enfant.
 *
 * Le moment que la boucle de reconnaissance prépare : la tâche est faite, le
 * parent a réagi, et l'enfant voit la réaction en ouvrant l'app. L'autocollant
 * prend l'écran, le mot du parent le suit, et l'arc au-dessus dit de qui il
 * vient : « Message de Maman » (cf. CheerSenderArc, lib/cheerSender).
 *
 * Même mise en scène que RewardCelebrationModal (plein écran crème, halos à la
 * couleur de l'enfant), en plus doux : un seul envol de confettis, et rien du
 * tout si l'appareil demande moins d'animations.
 */
import { computed, nextTick, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import confetti from 'canvas-confetti'
import AppSheet from '@/components/common/AppSheet.vue'
import { Button } from '@/components/ui/button'
import { cheerSticker } from '@/config/cheerStickers'
import CheerStickerArt from '@/components/cheers/CheerStickerArt.vue'
import CheerSenderArc from '@/components/cheers/CheerSenderArc.vue'
import { cheerSenderLabel } from '@/lib/cheerSender'
import { getChildColorInfo } from '@/lib/colors'
import { useHaptics } from '@/composables/useHaptics'
import { playSound } from '@/lib/sound'
import type { Child } from '@/models/Child'

export interface CheerPayload {
  sticker: string
  message?: string | null
  sender_name?: string | null
  /** household_members.parent_type de l'expéditeur, à l'envoi (absent avant la 3.1.1). */
  sender_type?: string | null
  task_title?: string | null
}

const props = defineProps<{
  cheer: CheerPayload | null
  child?: Child | null
}>()

const emit = defineEmits<{ dismiss: [] }>()

const { t } = useI18n()
const haptics = useHaptics()

const isOpen = computed(() => !!props.cheer)
const sticker = computed(() => cheerSticker(props.cheer?.sticker))
const palette = computed(() => getChildColorInfo(props.child?.color || 'blue'))

const senderLabel = computed(() => cheerSenderLabel(props.cheer?.sender_type, props.cheer?.sender_name, t))
const signature = computed(() =>
  senderLabel.value ? t('cheer.messageFrom', { name: senderLabel.value }) : t('cheer.fromParent'))

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

// Au-dessus du panneau (19991), sous les dialogues (20000), comme les autres.
const CONFETTI_Z_INDEX = 19995

watch(isOpen, async (open) => {
  if (!open) return
  await nextTick()
  void haptics.notificationSuccess()
  void playSound('cheer')
  if (prefersReducedMotion()) return
  const p = palette.value
  setTimeout(() => {
    confetti({
      particleCount: 60,
      angle: 90,
      spread: 80,
      origin: { x: 0.5, y: 0.55 },
      colors: [p.hex300, p.hex500, p.hex900, '#FFC83D', '#FFFFFF'],
      zIndex: CONFETTI_Z_INDEX,
    })
  }, 250)
})
</script>

<template>
  <AppSheet :is-open="isOpen" mode="fullscreen" transparent :backdrop-dismiss="false"
    panel-class="reward-celebration-panel" backdrop-class="celebration-backdrop"
    @update:is-open="(v: boolean) => { if (!v) emit('dismiss') }">
    <div class="relative flex h-full w-full flex-col items-center justify-between overflow-hidden bg-warm px-6 py-10"
      data-testid="cheer-celebration">
      <div class="pointer-events-none absolute -top-20 -left-16 size-72 rounded-full opacity-40"
        :style="{ backgroundColor: palette.hex200, filter: 'blur(60px)' }" aria-hidden="true" />
      <div class="pointer-events-none absolute -bottom-24 -right-16 size-80 rounded-full opacity-40"
        :style="{ backgroundColor: palette.hex300, filter: 'blur(70px)' }" aria-hidden="true" />

      <!-- La place du haut, gardée : le bouton reste en bas, l'autocollant au milieu. -->
      <div class="pt-4" aria-hidden="true" />

      <div class="relative z-10 flex w-full flex-col items-center">
        <CheerSenderArc :label="senderLabel" :color="palette.hex500" :shade="palette.hex900" class="-mb-16"
          data-testid="cheer-sender" />
        <p class="sr-only">{{ signature }}</p>
        <CheerStickerArt :sticker="sticker.key" class="cheer-pop h-52 w-52" aria-hidden="true" />
        <!-- Le mot est écrit sur le sticker ; le titre reste pour les lecteurs
             d'écran. -->
        <h2 class="sr-only">{{ t(`cheer.stickers.${sticker.key}`) }}</h2>
        <p v-if="cheer?.message" class="cheer-rise mt-4 max-w-xs rounded-2xl border border-line bg-white px-4 py-3 text-center text-[16px] leading-snug text-ink"
          data-testid="cheer-message-shown">
          {{ cheer.message }}
        </p>
        <p v-if="cheer?.task_title" class="mt-3 max-w-xs text-center text-[13px] text-ink-3">
          {{ t('cheer.forTask', { task: cheer.task_title }) }}
        </p>
      </div>

      <Button data-testid="cheer-close" :color="palette.value" tactile size="lg" expand="block"
        class="relative z-10 max-w-md" @click="emit('dismiss')">
        {{ t('cheer.thanks') }}
      </Button>
    </div>
  </AppSheet>
</template>

<style scoped>
.cheer-pop {
  animation: cheer-pop 520ms cubic-bezier(0.34, 1.56, 0.64, 1) both;
}
.cheer-rise {
  animation: cheer-rise 420ms 180ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
}
@keyframes cheer-pop {
  from { transform: scale(0.4) rotate(-8deg); opacity: 0; }
  to { transform: scale(1) rotate(0); opacity: 1; }
}
@keyframes cheer-rise {
  from { transform: translateY(12px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .cheer-pop, .cheer-rise { animation: none; }
}
</style>
